// src/utils/exportExcel.js
// Shared STYLED Excel export (exceljs): branded title, green header row,
// zebra rows, borders, auto-filter, frozen header, number formats.
//
// exportStyledExcel({
//   filename: 'sales_2026-06',           // .xlsx appended automatically
//   sheetName: 'Sales',
//   title: 'Sales Report (USD)',
//   subtitle: 'Period: 01 Jun — 11 Jun · 42 documents',
//   columns: [{ header, key, width?, numFmt?, align? }],
//   rows: [{ ...byKey, __bold?, __fill? ('FFEAF6F3') }],
// })
import ExcelJS from 'exceljs';

const GREEN = 'FF2E9F86';
const GREEN_DARK = 'FF1F7D68';
const ZEBRA = 'FFF6F8F8';

export async function exportStyledExcel({ filename, sheetName = 'Sheet1', title, subtitle, columns, rows }) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'MMM POS';
  wb.created = new Date();

  const headerRowIdx = title ? 3 : 1;
  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: 'frozen', ySplit: headerRowIdx }],
  });

  ws.columns = columns.map(c => ({ width: c.width || 16 }));

  // title block
  if (title) {
    ws.mergeCells(1, 1, 1, columns.length);
    const t = ws.getCell(1, 1);
    t.value = title;
    t.font = { bold: true, size: 14, color: { argb: 'FF111111' } };
    ws.getRow(1).height = 22;

    ws.mergeCells(2, 1, 2, columns.length);
    const s = ws.getCell(2, 1);
    s.value = subtitle || `Generated ${new Date().toLocaleDateString('en-GB')} — MMM POS`;
    s.font = { size: 10, color: { argb: 'FF6B7280' } };
  }

  // header row
  const hr = ws.getRow(headerRowIdx);
  columns.forEach((c, i) => {
    const cell = hr.getCell(i + 1);
    cell.value = c.header;
    cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN } };
    cell.alignment = { horizontal: c.align || 'left', vertical: 'middle' };
    cell.border = { bottom: { style: 'medium', color: { argb: GREEN_DARK } } };
  });
  hr.height = 20;

  // data rows
  rows.forEach((r, ri) => {
    const row = ws.getRow(headerRowIdx + 1 + ri);
    const isArr = Array.isArray(r);
    columns.forEach((c, ci) => {
      const cell = row.getCell(ci + 1);
      let v = isArr ? r[ci] : r[c.key];
      if (v === undefined || v === null) v = '';
      cell.value = v;
      if (c.numFmt && typeof v === 'number') cell.numFmt = c.numFmt;
      cell.alignment = { horizontal: c.align || (typeof v === 'number' ? 'right' : 'left'), vertical: 'middle' };
      if (!isArr && r.__fill) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: r.__fill } };
      } else if (ri % 2 === 1) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } };
      }
      if (!isArr && r.__bold) cell.font = { bold: true, size: 11 };
      cell.border = { bottom: { style: 'hair', color: { argb: 'FFE5E7EB' } } };
    });
  });

  // filter on the header row
  ws.autoFilter = {
    from: { row: headerRowIdx, column: 1 },
    to: { row: headerRowIdx, column: columns.length },
  };

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

export const r2 = (n) => Number(Number(n || 0).toFixed(2));
export const MONEY_FMT = '#,##0.00';
export const QTY_FMT = '0.###';
export const TOTAL_FILL = 'FFEAF6F3';
